import React, { useEffect, memo, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Tooltip } from 'appkit-react';

import '../../styles/modules/register.scss';
import LoginLogo from '@components/login-logo';
import { Form, Input, Button, Select, Modal } from 'antd';
import actions from '@flux/action';
import { setSessionStorage } from '@utils/windowUtil';
import { shouldShowInvalidActivationModal } from '@utils/activationRegistration';
// import { useHistory } from 'react-router-dom';
import { COUNTRY_CODES } from '../../components/charts/map/countryCodePhoneData';

// IAM v2: 16-64 chars, 3 of 4 (lower/upper/digit/special), allowed chars only, no leading/trailing space, no Unicode
const PASSWORD_MIN_LENGTH = 16;
const PASSWORD_MAX_LENGTH = 64;
const PASSWORD_ALLOWED_CHARS =
  /^[A-Za-z0-9 @#$%^&*\-_!+=[\]\\:',.?\/`~"();<>]+$/;
const PASSWORD_SPECIAL_CHARS = /[@#$%^&*\-_!+=[\]\\:',.?\/`~"();<>]/;

function validatePasswordIAMv2(value) {
  if (!value || typeof value !== 'string') return 'Please input your password!';
  const trimmed = value.trim();
  if (value !== trimmed)
    return 'Password must not have leading or trailing spaces.';
  if (trimmed.length < PASSWORD_MIN_LENGTH)
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (trimmed.length > PASSWORD_MAX_LENGTH)
    return `Password must be at most ${PASSWORD_MAX_LENGTH} characters.`;
  if (!PASSWORD_ALLOWED_CHARS.test(trimmed))
    return 'Password may only contain A-Z, a-z, 0-9, and the allowed special characters (no Unicode).';
  const hasLower = /[a-z]/.test(trimmed);
  const hasUpper = /[A-Z]/.test(trimmed);
  const hasDigit = /\d/.test(trimmed);
  const hasSpecial = PASSWORD_SPECIAL_CHARS.test(trimmed);
  const typeCount =
    (hasLower ? 1 : 0) +
    (hasUpper ? 1 : 0) +
    (hasDigit ? 1 : 0) +
    (hasSpecial ? 1 : 0);
  if (typeCount < 3)
    return 'Password must contain at least 3 of: lowercase letter, uppercase letter, number, special character.';
  return null;
}

function Register(props) {
  const { location, register, history, login, loading, fetchUserDetails } =
    props;
  const [countryCode, setCountryCode] = useState('+1');
  const [form] = Form.useForm();
  const [invalidLinkModalOpen, setInvalidLinkModalOpen] = useState(false);

  const extractedParams = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const activationId = (
      params.get('activationId') ||
      params.get('registrationToken') ||
      ''
    ).trim();
    return {
      activationToken: params.get('activationToken') || '',
      activationId,
      productId: params.get('productId') || '',
    };
  }, [location.search]);

  useEffect(() => {
    const activationId = extractedParams.activationId;
    if (!activationId) {
      return;
    }

    let cancelled = false;

    fetchUserDetails(activationId)
      .then((response) => {
        if (cancelled) return;
        if (shouldShowInvalidActivationModal(response)) {
          setInvalidLinkModalOpen(true);
          return;
        }
        if (response?.email) {
          form.setFieldsValue({ email: response.email });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setInvalidLinkModalOpen(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is stable; refetch only when activation id changes
  }, [extractedParams.activationId, fetchUserDetails]);

  const onFinish = (values) => {
    const { password, email, firstName, lastName, phoneNumber } = values;

    // Combine country code with phone number
    const fullPhoneNumber = `${countryCode}${phoneNumber}`;
    const { activationToken, activationId } = extractedParams;

    const v = {
      body: {
        password,
        email,
        activationToken,
        activationId,
        firstName,
        lastName,
        phoneNumber: fullPhoneNumber,
      },
    };

    // Dispatch the action and handle the promise
    register(v.body)
      .then((res) => {
        console.log('reg_succ : ', res);
        if (res === '200') {
          // message.success('Registration successful!');
          history.replace('/list');
        } else {
          // message.error('Registration failed!');
          console.log('Registration failed!');
        }
      })
      .catch((error) => {
        console.error('Registration error:', error);
        // message.error('An error occurred during registration');
      });
  };

  // useEffect(() => {
  //   if (!initialized) {
  //     return;
  //   }
  //   window.localStorage.setItem('accessToken', `Bearer ${userInfo?.uniqueId}`);
  //   window.localStorage.setItem('idToken', '___pseudo_id_token____');
  //   window.localStorage.setItem('expired', Date.parse(new Date()) + 3600000);
  //   window.localStorage.setItem('userInfo', JSON.stringify(userInfo));
  //   history.replace('/');
  // }, [userInfo]);

  //disabled for development purposes
  // if (isLogin) {
  //   history.push('/');
  // }

  // useEffect(() => {
  //   setInitialized(true);
  //   clearTokens();
  // }, []);

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleSignIn = () => {
    setSessionStorage('loginProgress', true);
    login && login('iam');
  };

  const handleInvalidLinkClose = () => {
    setInvalidLinkModalOpen(false);
    handleSignIn();
  };

  const handleCountryCodeChange = (value) => {
    setCountryCode(value);
  };

  // Enhanced filter function for search - works with both text and numbers
  const filterOption = (input, option) => {
    const searchText = input.toLowerCase().trim();

    // If search is empty, show all
    if (!searchText) return true;

    // Get all searchable fields
    const label = option.label?.toLowerCase() || '';
    const country = option.country?.toLowerCase() || '';
    const value = option.value?.toLowerCase() || '';

    // Remove + sign and spaces from search for number matching
    const searchNumber = searchText.replace(/\D/g, '');
    const optionNumber = value.replace(/\D/g, '');

    // Search by:
    // 1. Country name (text)
    // 2. Full label with emoji (text)
    // 3. Country code with + sign (number)
    // 4. Country code without + sign (number)
    // 5. First few characters of country name
    return (
      country.includes(searchText) || // "India" or "ind"
      label.includes(searchText) || // "🇮🇳 +91" or any part
      value.includes(searchText) || // "+91"
      value.includes(`+${searchText}`) || // "91" matches "+91"
      searchNumber === optionNumber || // "91" matches "+91"
      country.startsWith(searchText) // "Ger" matches "Germany"
    );
  };

  return (
    <div className="register-page-wrapper">
      <Modal
        className="register-invalid-link-modal"
        visible={invalidLinkModalOpen}
        title={null}
        closable={false}
        onCancel={handleInvalidLinkClose}
        footer={
          <Button
            type="primary"
            className="register-invalid-link-modal__login-btn"
            onClick={handleInvalidLinkClose}
          >
            Login
          </Button>
        }
        maskClosable
        keyboard
        width={400}
        centered
        destroyOnClose
      >
        <div className="register-invalid-link-modal__body">
          <span className="register-invalid-link-modal__badge">ERROR</span>
          <p className="register-invalid-link-modal__message">
            Oops! It looks like you followed an invalid/expired url. Please
            click below to login.
          </p>
        </div>
      </Modal>

      <div className="register-container">
        <LoginLogo />

        <div className="register-page">
          <div className="text">Register</div>
          <Form
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            name="register"
            autoComplete="off"
          >
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[
                { required: true, message: 'Please input your first name!' },
                {
                  pattern: /^[0-9a-zA-Z]+$/,
                  message: 'No special characters in name.',
                },
              ]}
            >
              <Input className="register-form-input" />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[
                { required: true, message: 'Please input your last name!' },
                {
                  pattern: /^[0-9a-zA-Z]+$/,
                  message: 'can not have special character',
                },
              ]}
            >
              <Input className="register-form-input" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[{ required: true, message: 'Please input your email!' }]}
            >
              <Input disabled className="register-form-input" />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[
                { required: true, message: 'Please input your phone number!' },
                {
                  pattern: /^[0-9\s\-\(\)\.]+$/,
                  message: 'Please enter a valid phone number!',
                },
                {
                  min: 7,
                  message: 'Phone number must be at least 7 digits!',
                },
                {
                  max: 14,
                  message: 'Phone number cannot exceed 14 digits!',
                },
              ]}
            >
              <Input
                addonBefore={
                  <Select
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                    options={COUNTRY_CODES}
                    className="country-code-select"
                    placeholder="Select"
                    showSearch
                    filterOption={filterOption}
                    optionFilterProp="children"
                    optionLabelProp="label"
                  />
                }
                placeholder="Enter your phone number"
                className="register-form-input"
                maxLength={14}
              />
            </Form.Item>

            <Form.Item
              label={
                <div className="form-item-password">
                  <div className="left">Password</div>
                  <div className="right">
                    <Tooltip
                      content={[
                        <div className="password-tooltip" key={1}>
                          <strong>Password requirements (IAM v2)</strong>
                          <br />
                        </div>,
                        <div className="password-tooltip">
                          1. Length: 16–64 characters
                          <br />
                          2. At least 3 of: lowercase, uppercase, number,
                          special character
                          <br />
                          3. Allowed: A–Z, a–z, 0–9, and @ # $ % ^ & * - _ ! + =
                          [ ] | \ : &apos; , . ? / ` ~ &quot; ( ) ; &lt; &gt;
                          <br />
                          4. Space allowed inside only (no leading/trailing
                          spaces)
                          <br />
                          5. No Unicode characters
                        </div>,
                      ]}
                      placement="top"
                      tooltipTheme="dark"
                      className="auth-tooltip"
                    >
                      <span className="icon-information-fill appkiticon" />
                    </Tooltip>
                  </div>
                </div>
              }
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' },
                {
                  validator(_, value) {
                    // Don't validate when empty – let the required rule show its message once (avoids duplicate "Please input your password!")
                    if (!value || typeof value !== 'string' || !value.trim()) {
                      return Promise.resolve();
                    }
                    const err = validatePasswordIAMv2(value);
                    if (err) return Promise.reject(err);
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input.Password
                className="register-form-input-password"
                maxLength={PASSWORD_MAX_LENGTH}
              />
            </Form.Item>

            <Form.Item
              name="confirm"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password!',
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      'The two passwords that you entered do not match!',
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                className="register-form-input-password"
                maxLength={PASSWORD_MAX_LENGTH}
              />
            </Form.Item>

            <Form.Item>
              <Button
                className="btn-wrapper sign_up_register_btn"
                htmlType="submit"
                type="primary"
                loading={loading}
              >
                Sign up
              </Button>
            </Form.Item>

            <div className="footer-login">
              Already have an account?{' '}
              <Button type="link" className="signin-btn" onClick={handleSignIn}>
                Sign in
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = ({ login, common }) => ({
  userInfo: login.userInfo,
  loading: common.isLoading,
});

const mapDispatchToProps = (dispatch) => ({
  register(p) {
    return dispatch(actions.register(p));
  },
  login(p) {
    dispatch(actions.login(p));
  },
  fetchUserDetails(activationId) {
    return dispatch(actions.fetchUserDetails(activationId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(Register));
